import { useCallback, useEffect, useState } from "react";
import { apiClient, ApiError, NotAuthenticatedError } from "../api/client.js";
import type {
  DaemonConfigResponse,
  DaemonDisk,
  DaemonMachine,
} from "../api/types.js";
import { AddConventionModal } from "../components/add-convention-modal.js";
import { DisksList } from "../components/disks-list.js";
import { SectionHeader } from "../components/help-button.js";
import { HELP } from "../components/help-content.js";
import { MachineSelector } from "../components/machine-selector.js";
import {
  PacingModeSelector,
  type PacingMode,
} from "../components/pacing-mode-selector.js";
import { SourceRootConventionEditor } from "../components/source-root-convention-editor.js";
import { SourceRootsEditor } from "../components/source-roots-editor.js";

// 2.5.E.2 (vaihe 2.5): /levyt-sivu — koneiden hallinta + source-rootit
// + USB-levyjen tila. Web-UI:n pääosin interaktiivinen näkymä daemon-
// konfiguraation hallintaan.

type Status = "idle" | "loading" | "error" | "ready";

export function Levyt() {
  const [machines, setMachines] = useState<DaemonMachine[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [config, setConfig] = useState<DaemonConfigResponse | null>(null);
  const [disks, setDisks] = useState<DaemonDisk[]>([]);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [savingMessage, setSavingMessage] = useState<string | null>(null);
  const [showAddConvention, setShowAddConvention] = useState(false);

  // Hae koneet ensimmäisessä renderissä + 60s refresh-intervallilla.
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setStatus((s) => (s === "ready" ? "ready" : "loading"));
      setError(null);
      try {
        const res = await apiClient.listMachines();
        if (cancelled) return;
        setMachines(res.machines);
        setStatus("ready");
        setSelectedId((prev) => {
          if (prev && res.machines.some((m) => m.id === prev)) return prev;
          return res.machines[0]?.id ?? null;
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotAuthenticatedError) {
          setError("Ei kirjautunut. Kirjaudu ensin sisaan.");
        } else if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
        setStatus("error");
      }
    }

    void load();
    const interval = setInterval(() => void load(), 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Hae config + disks valitulle koneelle.
  useEffect(() => {
    if (!selectedId) {
      setConfig(null);
      setDisks([]);
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        const [cfgRes, disksRes] = await Promise.all([
          apiClient.getMachineConfig(selectedId!),
          apiClient.getMachineDisks(selectedId!),
        ]);
        if (cancelled) return;
        setConfig(cfgRes);
        setDisks(disksRes.disks);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      }
    }
    void load();
    const interval = setInterval(() => void load(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [selectedId]);

  const handleSourceRootsChange = useCallback(
    async (next: string[]) => {
      if (!selectedId) return;
      setSavingMessage("Tallennetaan…");
      try {
        const res = await apiClient.updateMachineConfig(selectedId, {
          sourceRoots: next,
        });
        setConfig(res);
        setSavingMessage("Tallennettu — daemon havaitsee muutoksen alle 60s.");
        setTimeout(() => setSavingMessage(null), 4000);
      } catch (err) {
        setSavingMessage(
          `Virhe: ${err instanceof Error ? err.message : String(err)}`
        );
      }
    },
    [selectedId]
  );

  const handlePacingChange = useCallback(
    async (mode: PacingMode) => {
      if (!selectedId) return;
      setSavingMessage("Tallennetaan…");
      try {
        const res = await apiClient.updateMachineConfig(selectedId, {
          pacingMode: mode,
        });
        setConfig(res);
        setSavingMessage("Tallennettu.");
        setTimeout(() => setSavingMessage(null), 3000);
      } catch (err) {
        setSavingMessage(
          `Virhe: ${err instanceof Error ? err.message : String(err)}`
        );
      }
    },
    [selectedId]
  );

  // 3.A.4: konventio-päivitys per source-root.
  const handleConventionChange = useCallback(
    async (sourceRoot: string, convention: string, autoCreate: boolean) => {
      if (!selectedId) return;
      setSavingMessage("Tallennetaan…");
      try {
        const opts = (config?.config.additionalOptions ?? {}) as Record<string, unknown>;
        const conventions = {
          ...((opts["sourceRootConventions"] as Record<string, string> | undefined) ?? {}),
        };
        if (convention.trim().length === 0) {
          delete conventions[sourceRoot];
        } else {
          conventions[sourceRoot] = convention;
        }
        const newOpts: Record<string, unknown> = {
          ...opts,
          sourceRootConventions: conventions,
          autoCreateSidecarFromConvention: autoCreate,
        };
        const res = await apiClient.updateMachineConfig(selectedId, {
          additionalOptions: newOpts,
        });
        setConfig(res);
        setSavingMessage("Tallennettu.");
        setTimeout(() => setSavingMessage(null), 3000);
      } catch (err) {
        setSavingMessage(
          `Virhe: ${err instanceof Error ? err.message : String(err)}`
        );
      }
    },
    [selectedId, config]
  );

  if (status === "loading" && machines.length === 0) {
    return <PageWrap><p className="text-slate-400">Ladataan…</p></PageWrap>;
  }

  if (status === "error") {
    return (
      <PageWrap>
        <p className="text-red-400">{error ?? "Tuntematon virhe"}</p>
      </PageWrap>
    );
  }

  if (machines.length === 0) {
    return (
      <PageWrap>
        <p className="text-slate-400">
          Ei vielä rekisteröityjä koneita. Käynnistä daemon Macilla — se
          rekisteröityy automaattisesti ja ilmestyy tähän listaan.
        </p>
      </PageWrap>
    );
  }

  const selected = machines.find((m) => m.id === selectedId) ?? null;

  return (
    <PageWrap>
      <MachineSelector
        machines={machines}
        selectedId={selectedId}
        onSelect={setSelectedId}
      />

      {selected && (
        <div className="mt-1 text-xs text-slate-500">
          Hostname {selected.hostname ?? "—"} •{" "}
          {selected.osPlatform ?? "—"} {selected.osVersion ?? ""} • viimeisin
          yhteys {formatRelative(selected.lastSeenAt)}
        </div>
      )}

      {savingMessage && (
        <div className="mt-3 rounded bg-slate-800 px-3 py-2 text-sm text-slate-200">
          {savingMessage}
        </div>
      )}

      <section className="mt-6 rounded-lg border border-slate-700 bg-slate-900 p-6">
        <SectionHeader title="Source-rootit" help={HELP.sourceRoots!.body} />
        <p className="mt-1 text-xs text-slate-500">
          Polut joita daemon seuraa muutoksien varalta. Vain /Users, /Volumes,
          /home, /mnt sallittu (system-paths torjutaan).
        </p>
        <div className="mt-4">
          <SourceRootsEditor
            paths={config?.config.sourceRoots ?? []}
            observedPaths={null}
            onChange={handleSourceRootsChange}
            disabled={!config}
            {...(selectedId ? { machineId: selectedId } : {})}
          />
        </div>
      </section>

      <section className="mt-6 rounded-lg border border-slate-700 bg-slate-900 p-6">
        <SectionHeader title="Pacing" help={HELP.pacing!.body} />
        <p className="mt-1 text-xs text-slate-500">
          Thumbnail-backfill:n nopeus. Vaiheessa 2.5 muutos vaatii daemon-
          restartin tullakseen voimaan.
        </p>
        <div className="mt-4">
          <PacingModeSelector
            value={(config?.config.pacingMode as PacingMode) ?? "polite"}
            onChange={handlePacingChange}
            disabled={!config}
          />
        </div>
      </section>

      {(() => {
        const opts = (config?.config.additionalOptions ?? {}) as Record<string, unknown>;
        const convs =
          (opts["sourceRootConventions"] as Record<string, string> | undefined) ?? {};
        const autoCreate = Boolean(opts["autoCreateSidecarFromConvention"]);
        const allRoots = config?.config.sourceRoots ?? [];
        // Vain ne source-rootit joilla on konventio asetettu (ei-tyhjä)
        const rootsWithConvention = allRoots.filter(
          (r) => typeof convs[r] === "string" && convs[r]!.trim().length > 0
        );
        // Vapaat source-rootit lisäys-modaaliin
        const availableRoots = allRoots.filter(
          (r) => !rootsWithConvention.includes(r)
        );

        return (
          <section className="mt-6 rounded-lg border border-slate-700 bg-slate-900 p-6">
            <div className="flex items-center justify-between gap-3">
              <SectionHeader
                title="Projekti-konventiot"
                help={HELP.conventions!.body}
              />
              <button
                type="button"
                onClick={() => setShowAddConvention(true)}
                disabled={!config || allRoots.length === 0}
                className="rounded-md bg-cyan-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
              >
                + Lisää konventio
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {rootsWithConvention.length === 0 ? (
                <p className="text-sm italic text-slate-500">
                  Ei aktiivisia konventioita. Paina{" "}
                  <strong>Lisää konventio</strong> kun haluat, että daemon
                  tunnistaa kansiot automaattisesti projekteiksi.
                </p>
              ) : (
                rootsWithConvention.map((root) => (
                  <SourceRootConventionEditor
                    key={root}
                    sourceRoot={root}
                    convention={convs[root] ?? ""}
                    autoCreate={autoCreate}
                    onChange={(c, ac) => handleConventionChange(root, c, ac)}
                    onRemove={() => handleConventionChange(root, "", autoCreate)}
                    disabled={!config}
                  />
                ))
              )}
            </div>

            {showAddConvention && (
              <AddConventionModal
                availableRoots={availableRoots}
                onAdd={async (root, conv, ac) => {
                  await handleConventionChange(root, conv, ac);
                }}
                onClose={() => setShowAddConvention(false)}
                {...(selectedId ? { machineId: selectedId } : {})}
              />
            )}
          </section>
        );
      })()}

      <section className="mt-6 rounded-lg border border-slate-700 bg-slate-900 p-6">
        <SectionHeader title="Levyt" help={HELP.disks!.body} />
        <p className="mt-1 text-xs text-slate-500">
          Daemon raportoi levyt 60 sekunnin valein. Vain viimeisten 10 minuutin
          sisällä raportoituneet rivit näkyvät.
        </p>
        <div className="mt-4">
          <DisksList disks={disks} />
        </div>
      </section>
    </PageWrap>
  );
}

function PageWrap({ children }: { children: React.ReactNode }) {
  return (
    <section className="mx-auto max-w-4xl px-6 py-10">
      <h2 className="text-2xl font-semibold tracking-tight text-slate-100">Levyt</h2>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s sitten`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min sitten`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h sitten`;
  return `${Math.floor(h / 24)} pv sitten`;
}
